import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { wellnessService, WELLNESS_DOMAINS } from "@/services/wellnessService";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
  Sparkles,
  Save,
  Target,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Volume2,
  VolumeX,
  Plus,
  X,
  Lightbulb,
  Heart,
  Compass,
  Star,
} from "lucide-react";
interface Vital7DashboardProps {
  compact?: boolean;
}

interface IdentityCreed {
  id?: string;
  userId: string;
  title: string;
  content: string;
  coreValues: string[];
  strengths: string[];
  purpose: string;
  vision: string;
}

const IDENTITY_PROMPTS = [
  "I am someone who...",
  "My greatest strength is...",
  "I believe deeply in...",
  "My purpose in life is...",
];

const CONFESSION_PROMPTS = [
  "Today I acknowledge...",
  "I am grateful for...",
  "I need to improve in...",
  "I commit to...",
];

export function Vital7Dashboard(_props: Vital7DashboardProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [wellnessOpen, setWellnessOpen] = useState(false);
  const [whoAmIOpen, setWhoAmIOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [localScores, setLocalScores] = useState<Record<string, number>>({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState<"creed" | "confession">("creed");
  const [creed, setCreed] = useState<IdentityCreed>({
    userId: user?.id || "",
    title: "My Identity Creed",
    content: "",
    coreValues: [],
    strengths: [],
    purpose: "",
    vision: "",
  });
  const [confession, setConfession] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newStrength, setNewStrength] = useState("");

  const { data: todayScore, isLoading } = useQuery({
    queryKey: ["wellness-today", user?.id],
    queryFn: () => wellnessService.getTodayScore(user!.id),
    enabled: !!user?.id,
  });

  const { data: recentScores } = useQuery({
    queryKey: ["wellness-recent", user?.id],
    queryFn: () => wellnessService.getRecentScores(user!.id, 365),
    enabled: !!user?.id,
  });

  const { data: savedCreed } = useQuery({
    queryKey: ["identity-creed", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("identity_creed")
        .select("id, user_id, title, content, core_values, strengths, purpose, vision, created_at, updated_at")
        .eq("user_id", user!.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching identity creed:", error);
        return null;
      }
      return data;
    },
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (savedCreed) {
      setCreed({
        id: savedCreed.id,
        userId: savedCreed.user_id,
        title: savedCreed.title || "My Identity Creed",
        content: savedCreed.content || "",
        coreValues: savedCreed.core_values || [],
        strengths: savedCreed.strengths || [],
        purpose: savedCreed.purpose || "",
        vision: savedCreed.vision || "",
      });
    }
  }, [savedCreed]);

  const { sevenDayAvg, twelveMonthAvg } = useMemo(() => {
    if (!recentScores || recentScores.length === 0) {
      return { sevenDayAvg: null, twelveMonthAvg: null };
    }

    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const twelveMonthsAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

    const last7Days = recentScores.filter(s => new Date(s.date) >= sevenDaysAgo);
    const last12Months = recentScores.filter(s => new Date(s.date) >= twelveMonthsAgo);

    const calcAvg = (scores: typeof recentScores) => {
      if (scores.length === 0) return null;
      const total = scores.reduce((sum, s) => sum + wellnessService.calculateOverallScore(s), 0);
      return Math.round((total / scores.length) * 10) / 10;
    };

    return {
      sevenDayAvg: calcAvg(last7Days),
      twelveMonthAvg: calcAvg(last12Months),
    };
  }, [recentScores]);

  const saveMutation = useMutation({
    mutationFn: async (scores: Record<string, number>) => {
      return wellnessService.updateScore(user!.id, {
        userId: user!.id,
        date: new Date(),
        physical: scores.physical?.toString() || "5",
        emotional: scores.emotional?.toString() || "5",
        spiritual: scores.spiritual?.toString() || "5",
        mental: scores.mental?.toString() || "5",
        relational: scores.relational?.toString() || "5",
        financial: scores.financial?.toString() || "5",
        vocational: scores.vocational?.toString() || "5",
        whoAmI: scores.whoAmI?.toString() || "5",
        notes: notes || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wellness-today"] });
      queryClient.invalidateQueries({ queryKey: ["wellness-recent"] });
      setWellnessOpen(false);
    },
  });

  const saveCreedMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        user_id: user!.id,
        title: creed.title,
        content: creed.content,
        core_values: creed.coreValues,
        strengths: creed.strengths,
        purpose: creed.purpose,
        vision: creed.vision,
        updated_at: new Date().toISOString(),
      };

      if (creed.id) {
        const { error } = await supabase
          .from("identity_creed")
          .update(payload)
          .eq("id", creed.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("identity_creed")
          .insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["identity-creed"] });
    },
  });

  const getScoreValue = (domain: string): number => {
    if (localScores[domain] !== undefined) {
      return Math.round(localScores[domain] * 10) / 10;
    }
    if (todayScore) {
      return parseFloat((todayScore as unknown as Record<string, string>)[domain] || "5");
    }
    return 5;
  };

  const handleSliderChange = (domain: string, value: number[]) => {
    const roundedValue = Math.round(value[0] * 10) / 10;
    setLocalScores((prev) => ({ ...prev, [domain]: roundedValue }));
  };

  const handleSave = () => {
    const scores: Record<string, number> = {};
    for (const domain of WELLNESS_DOMAINS) {
      scores[domain.key] = getScoreValue(domain.key);
    }
    scores.whoAmI = getScoreValue("whoAmI");
    saveMutation.mutate(scores);
  };

  const overallScore = todayScore 
    ? Math.round(wellnessService.calculateOverallScore(todayScore) * 10) / 10
    : 5;
  const scoreInfo = wellnessService.getScoreLabel(overallScore);

  const speak = (text: string) => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.85;
      utterance.pitch = 1;
      utterance.onend = () => setIsPlaying(false);
      utterance.onerror = () => setIsPlaying(false);
      setIsPlaying(true);
      speechSynthesis.speak(utterance);
    }
  };

  const stopSpeaking = () => {
    speechSynthesis.cancel();
    setIsPlaying(false);
  };

  const togglePlayback = () => {
    if (isPlaying) {
      stopSpeaking();
    } else {
      const fullText = buildFullCreedText();
      speak(fullText);
    }
  };

  const buildFullCreedText = () => {
    let text = creed.title + ". ";
    if (creed.content) text += creed.content + ". ";
    if (creed.coreValues.length > 0) {
      text += "My core values are: " + creed.coreValues.join(", ") + ". ";
    }
    if (creed.strengths.length > 0) {
      text += "My strengths include: " + creed.strengths.join(", ") + ". ";
    }
    if (creed.purpose) text += "My purpose is: " + creed.purpose + ". ";
    if (creed.vision) text += "My vision is: " + creed.vision + ". ";
    return text;
  };

  const addValue = () => {
    if (newValue.trim() && !creed.coreValues.includes(newValue.trim())) {
      setCreed(prev => ({
        ...prev,
        coreValues: [...prev.coreValues, newValue.trim()],
      }));
      setNewValue("");
    }
  };

  const removeValue = (value: string) => {
    setCreed(prev => ({
      ...prev,
      coreValues: prev.coreValues.filter(v => v !== value),
    }));
  };

  const addStrength = () => {
    if (newStrength.trim() && !creed.strengths.includes(newStrength.trim())) {
      setCreed(prev => ({
        ...prev,
        strengths: [...prev.strengths, newStrength.trim()],
      }));
      setNewStrength("");
    }
  };

  const removeStrength = (strength: string) => {
    setCreed(prev => ({
      ...prev,
      strengths: prev.strengths.filter(s => s !== strength),
    }));
  };

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            Life Wellness & Identity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-8 bg-muted rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden border-2 border-amber-500/20">
      <CardHeader className="pb-2 bg-gradient-to-r from-amber-500/5 to-purple-500/5">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-amber-500 to-purple-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span>Life Wellness & Identity</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span 
              className="font-bold" 
              style={{ color: scoreInfo.color }}
            >
              {overallScore}
            </span>
            <span className="text-xs text-muted-foreground">
              {scoreInfo.label}
            </span>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-3 space-y-3">
        {/* 7 Domain Wellness Check Section */}
        <Collapsible open={wellnessOpen} onOpenChange={setWellnessOpen}>
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <div className="text-left">
                  <div className="font-semibold text-sm">7 Domain Wellness Check</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      7d: {sevenDayAvg ?? "—"}
                    </span>
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      12m: {twelveMonthAvg ?? "—"}
                    </span>
                  </div>
                </div>
              </div>
              {wellnessOpen ? (
                <ChevronUp className="w-5 h-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="pt-3">
            <div className="space-y-4 p-3 rounded-lg border bg-background">
              {WELLNESS_DOMAINS.map((domain) => {
                const score = getScoreValue(domain.key);
                return (
                  <div key={domain.key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{domain.icon}</span>
                        <div>
                          <div className="font-medium text-sm">{domain.label}</div>
                          <div className="text-xs text-muted-foreground">
                            {domain.description}
                          </div>
                        </div>
                      </div>
                      <span
                        className="text-sm font-bold min-w-[3rem] text-right"
                        style={{ color: domain.color }}
                      >
                        {score}/10
                      </span>
                    </div>
                    <Slider
                      value={[score]}
                      min={1}
                      max={10}
                      step={0.1}
                      onValueChange={(value) => handleSliderChange(domain.key, value)}
                      className="cursor-pointer"
                    />
                  </div>
                );
              })}

              <div className="space-y-2 pt-2">
                <label className="text-sm font-medium">Notes (optional)</label>
                <Textarea
                  placeholder="How are you feeling today?"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="min-h-[60px]"
                />
              </div>

              <Button 
                onClick={handleSave} 
                disabled={saveMutation.isPending} 
                className="w-full"
              >
                <Save className="w-4 h-4 mr-2" />
                {saveMutation.isPending ? "Saving..." : "Save Check-In"}
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* WHO AM I Section */}
        <Collapsible open={whoAmIOpen} onOpenChange={setWhoAmIOpen}>
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-amber-500/10 to-purple-500/10 hover:from-amber-500/20 hover:to-purple-500/20 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-amber-500 to-purple-500 flex items-center justify-center">
                  <Target className="w-4 h-4 text-white" />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-sm">WHO AM I</div>
                  <div className="text-xs text-muted-foreground">
                    Identity Creed & Truthful Confession
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePlayback();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      togglePlayback();
                    }
                  }}
                  className="h-8 w-8 flex items-center justify-center rounded-md hover-elevate active-elevate-2 cursor-pointer"
                >
                  {isPlaying ? (
                    <VolumeX className="w-4 h-4" />
                  ) : (
                    <Volume2 className="w-4 h-4" />
                  )}
                </div>
                {whoAmIOpen ? (
                  <ChevronUp className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="pt-3">
            <div className="p-3 rounded-lg border bg-background">
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "creed" | "confession")}>
                <TabsList className="grid grid-cols-2 w-full mb-4">
                  <TabsTrigger value="creed" className="flex items-center gap-2 text-xs">
                    <Star className="w-3 h-3" />
                    Identity Creed
                  </TabsTrigger>
                  <TabsTrigger value="confession" className="flex items-center gap-2 text-xs">
                    <Heart className="w-3 h-3" />
                    Truthful Confession
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="creed" className="space-y-4">
                  <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-start gap-2 text-xs">
                      <Lightbulb className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
                      <p className="text-muted-foreground">
                        Your Identity Creed is your personal declaration of who you are and where you're going.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium">Title</label>
                    <Input
                      value={creed.title}
                      onChange={(e) => setCreed(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="My Identity Creed"
                      className="h-8 text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium">Your Declaration</label>
                    <Textarea
                      value={creed.content}
                      onChange={(e) => setCreed(prev => ({ ...prev, content: e.target.value }))}
                      placeholder="I am a person of integrity and purpose..."
                      className="min-h-[80px] text-sm"
                    />
                    <div className="flex flex-wrap gap-1">
                      {IDENTITY_PROMPTS.map((prompt, i) => (
                        <Badge
                          key={i}
                          variant="outline"
                          className="cursor-pointer hover:bg-primary/10 text-xs"
                          onClick={() => setCreed(prev => ({ 
                            ...prev, 
                            content: prev.content + (prev.content ? " " : "") + prompt 
                          }))}
                        >
                          {prompt}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium flex items-center gap-1">
                      <Compass className="w-3 h-3" />
                      Core Values
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addValue()}
                        placeholder="Add a core value..."
                        className="flex-1 h-8 text-sm"
                      />
                      <Button onClick={addValue} size="icon" variant="outline" className="h-8 w-8">
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {creed.coreValues.map((value, i) => (
                        <Badge key={i} className="flex items-center gap-1 bg-amber-500/20 text-amber-700 hover:bg-amber-500/30 text-xs">
                          {value}
                          <X className="w-2 h-2 cursor-pointer" onClick={() => removeValue(value)} />
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Strengths
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={newStrength}
                        onChange={(e) => setNewStrength(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addStrength()}
                        placeholder="Add a strength..."
                        className="flex-1 h-8 text-sm"
                      />
                      <Button onClick={addStrength} size="icon" variant="outline" className="h-8 w-8">
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {creed.strengths.map((strength, i) => (
                        <Badge key={i} className="flex items-center gap-1 bg-purple-500/20 text-purple-700 hover:bg-purple-500/30 text-xs">
                          {strength}
                          <X className="w-2 h-2 cursor-pointer" onClick={() => removeStrength(strength)} />
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium">My Purpose</label>
                    <Textarea
                      value={creed.purpose}
                      onChange={(e) => setCreed(prev => ({ ...prev, purpose: e.target.value }))}
                      placeholder="To inspire and empower others..."
                      className="min-h-[60px] text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium">My Vision</label>
                    <Textarea
                      value={creed.vision}
                      onChange={(e) => setCreed(prev => ({ ...prev, vision: e.target.value }))}
                      placeholder="I see myself as..."
                      className="min-h-[60px] text-sm"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={() => saveCreedMutation.mutate()} 
                      disabled={saveCreedMutation.isPending}
                      className="flex-1"
                      size="sm"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      {saveCreedMutation.isPending ? "Saving..." : "Save Creed"}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={togglePlayback}
                      size="sm"
                    >
                      {isPlaying ? <VolumeX className="w-3 h-3 mr-1" /> : <Volume2 className="w-3 h-3 mr-1" />}
                      {isPlaying ? "Stop" : "Listen"}
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="confession" className="space-y-4">
                  <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <div className="flex items-start gap-2 text-xs">
                      <Heart className="w-3 h-3 text-purple-500 mt-0.5 shrink-0" />
                      <p className="text-muted-foreground">
                        A truthful confession is a moment of honest reflection. Acknowledge where you are and commit to growth.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium">Today's Confession</label>
                    <Textarea
                      value={confession}
                      onChange={(e) => setConfession(e.target.value)}
                      placeholder="Today I acknowledge..."
                      className="min-h-[150px] text-sm"
                    />
                    <div className="flex flex-wrap gap-1">
                      {CONFESSION_PROMPTS.map((prompt, i) => (
                        <Badge
                          key={i}
                          variant="outline"
                          className="cursor-pointer hover:bg-primary/10 text-xs"
                          onClick={() => setConfession(prev => prev + (prev ? " " : "") + prompt)}
                        >
                          {prompt}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <Button 
                    variant="outline"
                    onClick={() => speak(confession)}
                    disabled={!confession.trim()}
                    className="w-full"
                    size="sm"
                  >
                    {isPlaying ? <VolumeX className="w-3 h-3 mr-1" /> : <Volume2 className="w-3 h-3 mr-1" />}
                    Speak My Confession
                  </Button>
                </TabsContent>
              </Tabs>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Quick Stats when both collapsed */}
        {!wellnessOpen && !whoAmIOpen && (
          <div className="grid grid-cols-7 gap-1 pt-2">
            {WELLNESS_DOMAINS.map((domain) => {
              const score = getScoreValue(domain.key);
              return (
                <div
                  key={domain.key}
                  className="flex flex-col items-center"
                  title={`${domain.label}: ${score}/10`}
                >
                  <span className="text-lg">{domain.icon}</span>
                  <div
                    className="w-full h-1.5 rounded-full mt-1"
                    style={{
                      background: `linear-gradient(to right, ${domain.color} ${score * 10}%, hsl(var(--muted)) ${score * 10}%)`,
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
